/**
 * Colour for the footer's "Powered by SWiiFT Studio" mark.
 *
 * Must stay in step with fe-client-swiift-studio's `poweredByInk` and its
 * `isDarkColor` (brightness <= 168), or the CMS preview and the live page
 * disagree: a mid teal like #6F9590 (137) used to show a white logo in the
 * CMS and a black one here, because this repo's shared isDarkColor cuts at 128.
 *
 * `chosen` is the footer's `poweredByColor`. Unset keeps the automatic
 * white-on-dark / black-on-light. `custom` tells the caller to tint the logo.
 */
const POWERED_BY_DARK_MAX = 168;

const brightness = (color: string): number | null => {
  const c = color.trim();
  const hex = c.match(/^#([0-9a-f]{3}|[0-9a-f]{6})([0-9a-f]{2})?$/i);
  if (hex) {
    const h =
      hex[1].length === 3
        ? hex[1]
            .split("")
            .map((x) => x + x)
            .join("")
        : hex[1];
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return (r * 299 + g * 587 + b * 114) / 1000;
  }
  const rgb = c.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (rgb) {
    return (+rgb[1] * 299 + +rgb[2] * 587 + +rgb[3] * 114) / 1000;
  }
  return null;
};

const isCssColour = (value: string) =>
  value.startsWith("#") || value.startsWith("rgb") || value.startsWith("hsl");

export const poweredByInk = (
  chosen: string | undefined | null,
  background: string | undefined | null
): { color: string; custom: boolean } => {
  const pick = chosen?.trim() ?? "";
  if (pick && isCssColour(pick)) return { color: pick, custom: true };
  const b = background ? brightness(background) : null;
  // Unknown backgrounds count as dark, as the CMS does.
  const dark = b === null || b <= POWERED_BY_DARK_MAX;
  return { color: dark ? "#FFFFFF" : "#000000", custom: false };
};
